// src/features/media/components/ClipRangeSelector.tsx

import { ActionIcon, Box, Stack, Group, Text, RangeSlider, Button, Tooltip } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { MediaUtils } from '../MediaUtils';
import { TimecodeInput } from './TimecodeInput';

interface ClipRangeSelectorProps {
  duration: number; // Total video duration in seconds
  startSecond: number; // Current in point
  endSecond: number; // Current out point
  playerTime: number | null; // Preview player's reported time; null until it reports
  onChange: (start: number, end: number) => void;
  onSetIn?: () => void; // Set in point at current player time
  onSetOut?: () => void; // Set out point at current player time
  onSeekPreview?: (second: number) => void; // Cue the preview player to a point, on explicit request
}

interface SetPointButtonProps {
  point: 'in' | 'out';
  playerTime: number | null;
  startSecond: number;
  endSecond: number;
  duration: number;
  onClick: () => void;
}

/**
 * Capture button for one clip point: it overwrites the point with the
 * preview's CURRENT POSITION — it does not confirm the value already there.
 * The bare word "Set" read as "apply my dragged value", so a click silently
 * threw away a hand-positioned handle and yanked it back to the playhead
 * (customer report 2026-08-30: "it will move the bar to 0"). The live
 * timecode now rides on the label, so the capture is visible before the
 * click, and an out-of-order capture says why instead of no-opping.
 *
 * Inactive until the preview has reported a time — capturing before that
 * would commit the 0:00 placeholder over the user's selection. Uses
 * data-disabled (not disabled) so the explanatory tooltip still receives
 * mouse events (Mantine pattern).
 */
function SetPointButton({
  point,
  playerTime,
  startSecond,
  endSecond,
  duration,
  onClick,
}: SetPointButtonProps) {
  const other = point === 'in' ? 'Out' : 'In';
  const block =
    playerTime === null
      ? null
      : MediaUtils.clipCaptureBlock(point, playerTime, startSecond, endSecond, duration);
  const inactive = playerTime === null || block !== null;

  const at = playerTime === null ? null : MediaUtils.formatTimecode(playerTime);
  const label =
    playerTime === null
      ? 'Play or scrub the preview first'
      : block === null
        ? `Move ${point === 'in' ? 'In' : 'Out'} to the preview's position (${at})`
        : block === 'past-end'
          ? `The preview is past the end of the source`
          : `The preview is at ${at}, ${point === 'in' ? 'at or after' : 'at or before'} the ${other} point (${MediaUtils.formatTimecode(point === 'in' ? endSecond : startSecond)}). Move the preview, or move ${other} first.`;

  return (
    <Tooltip label={label} withArrow multiline w={260}>
      <Button
        variant="light"
        size="compact-xs"
        data-disabled={inactive || undefined}
        aria-disabled={inactive}
        style={{ fontVariantNumeric: 'tabular-nums' }}
        onClick={(e) => {
          if (inactive) {
            e.preventDefault();
            return;
          }
          onClick();
        }}
      >
        {at === null ? 'Set' : `Set ${at}`}
      </Button>
    </Tooltip>
  );
}

interface GoToPointButtonProps {
  second: number;
  label: string;
  onClick: () => void;
}

/**
 * "Show" cue button for one clip point. Cueing is an EXPLICIT action, never a
 * side effect of editing: a cue is a real seek in the embedded player, and
 * firing one per slider release / per arrow-key repeat buried the player in
 * seeks it could never finish (3.14.0 regression — customer report 2026-08-30,
 * 80-minute source). Editing In/Out is now free; the preview moves only here.
 */
function GoToPointButton({ second, label, onClick }: GoToPointButtonProps) {
  return (
    <Tooltip label={`Jump the preview to the ${label} point (${MediaUtils.formatTimecode(second)})`} withArrow>
      <ActionIcon
        variant="subtle"
        size="sm"
        color="gray"
        aria-label={`Jump the preview to the ${label} point`}
        onClick={onClick}
      >
        <IconEye size={16} />
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * Clip range selection: editable In/Out timecode fields for precision,
 * timeline slider for coarse selection and visual context.
 */
export function ClipRangeSelector({
  duration,
  startSecond,
  endSecond,
  playerTime,
  onChange,
  onSetIn,
  onSetOut,
  onSeekPreview,
}: ClipRangeSelectorProps) {
  const clipDuration = endSecond - startSecond;

  return (
    <Stack gap="xs">
      {/* Editable time fields with Set buttons */}
      <Group justify="space-between">
        <Group gap="xs">
          <Text size="sm" fw={500}>
            In:
          </Text>
          <TimecodeInput
            value={startSecond}
            min={0}
            max={endSecond - 1}
            ariaLabel="Clip in point"
            onCommit={(seconds) => onChange(seconds, endSecond)}
          />
          {onSetIn && (
            <SetPointButton
              point="in"
              playerTime={playerTime}
              startSecond={startSecond}
              endSecond={endSecond}
              duration={duration}
              onClick={onSetIn}
            />
          )}
          {onSeekPreview && (
            <GoToPointButton
              second={startSecond}
              label="In"
              onClick={() => onSeekPreview(startSecond)}
            />
          )}
        </Group>
        <Group gap="xs">
          {onSeekPreview && (
            <GoToPointButton
              second={endSecond}
              label="Out"
              onClick={() => onSeekPreview(endSecond)}
            />
          )}
          {onSetOut && (
            <SetPointButton
              point="out"
              playerTime={playerTime}
              startSecond={startSecond}
              endSecond={endSecond}
              duration={duration}
              onClick={onSetOut}
            />
          )}
          <Text size="sm" fw={500}>
            Out:
          </Text>
          <TimecodeInput
            value={endSecond}
            min={startSecond + 1}
            max={duration}
            ariaLabel="Clip out point"
            onCommit={(seconds) => onChange(startSecond, seconds)}
          />
        </Group>
      </Group>

      {/* Range slider with live playhead marker (player's current position) */}
      <Box pos="relative">
        <RangeSlider
          min={0}
          max={duration}
          step={1}
          minRange={1}
          value={[startSecond, endSecond]}
          onChange={([start, end]) => onChange(start, end)}
          label={(value) => MediaUtils.formatTimecode(value)}
          labelAlwaysOn={false}
          marks={[
            { value: 0, label: MediaUtils.formatTimecode(0) },
            { value: duration, label: MediaUtils.formatTimecode(duration) },
          ]}
          styles={{
            markLabel: { fontSize: 'var(--mantine-font-size-xs)' },
          }}
          mb="lg"
        />
        {playerTime !== null && duration > 0 && (
          <Box
            aria-hidden
            data-testid="clip-playhead"
            style={{
              position: 'absolute',
              // Thumb centers travel inset by half a thumb per side (16px md
              // thumb) — the marker must share that mapping or it drifts off
              // the thumbs toward the edges.
              left: `calc(8px + ${
                Math.min(Math.max(playerTime, 0), duration) / duration
              } * (100% - 16px))`,
              top: 2,
              height: 20,
              width: 2,
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--mantine-color-gray-6)',
              borderRadius: 1,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      {/* Duration display */}
      <Text size="sm" fw={500}>
        Clip Duration: {MediaUtils.formatTimecode(clipDuration)}
      </Text>
    </Stack>
  );
}
